"use client";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/client/api/axios";

export interface SubCategory {
  id: number;
  categoryId: number;
  cityId: number;
  name: string;
  nameSpanish: string | null;
  description: string;
  descriptionSpanish: string | null;
  selectedBannerImageApp: string | null;
  unselectedBannerImageApp: string | null;
  bannerImageWeb: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  category?: { id: number; name: string };
  city?: { id: number; name: string; state: string; country: string };
}

export type SubCategoryCreatePayload = {
  categoryId: number;
  cityId: number;
  name: string;
  description: string;
  nameSpanish?: string | null;
  descriptionSpanish?: string | null;
  selectedBannerImageApp?: string | null;
  unselectedBannerImageApp?: string | null;
  bannerImageWeb?: string | null;
};

const SUB_CATEGORIES_QUERY_KEY = ["sub-categories"] as const;

export type SubCategoriesQueryParams = {
  status?: "active" | "deactivate";
  cityId?: number;
  categoryId?: number;
};

async function fetchSubCategories(params?: SubCategoriesQueryParams) {
  const { data } = await api.get<{ success: boolean; data: SubCategory[] }>(
    "/sub-categories",
    { params: params ?? undefined },
  );
  return data.data ?? data;
}

async function fetchSubCategory(id: number) {
  const { data } = await api.get<{ success: boolean; data: SubCategory }>(
    `/sub-categories/${id}`,
  );
  return data.data ?? data;
}

async function addSubCategory(payload: SubCategoryCreatePayload) {
  const { data } = await api.post<{ success: boolean; data: SubCategory }>(
    "/sub-categories",
    payload,
  );
  return data.data ?? data;
}

async function updateSubCategory(
  id: number,
  payload: Partial<SubCategoryCreatePayload> & { isActive?: boolean },
) {
  const { data } = await api.put<{ success: boolean; data: SubCategory }>(
    `/sub-categories/${id}`,
    payload,
  );
  return data.data ?? data;
}

async function deleteSubCategory(id: number) {
  await api.delete(`/sub-categories/${id}`);
}

async function bulkDeleteSubCategories(ids: number[]) {
  await api.post("/sub-categories/bulk-delete", { ids });
}

async function toggleSubCategoryActive(id: number, isActive: boolean) {
  const { data } = await api.patch<{ success: boolean; data: SubCategory }>(
    `/sub-categories/${id}`,
    { isActive },
  );
  return data.data ?? data;
}

async function uploadSubCategoryImage(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post<{ success: boolean; url: string }>(
    "/sub-categories/upload-image",
    formData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
}

export function useSubCategoriesQuery(params?: SubCategoriesQueryParams) {
  return useQuery({
    queryKey: [...SUB_CATEGORIES_QUERY_KEY, params ?? "all"],
    queryFn: () => fetchSubCategories(params),
  });
}

export function useSubCategoryQuery(id: number | null) {
  return useQuery({
    queryKey: [...SUB_CATEGORIES_QUERY_KEY, id],
    queryFn: () => fetchSubCategory(id!),
    enabled: id != null && id > 0,
  });
}

export function useAddSubCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addSubCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUB_CATEGORIES_QUERY_KEY });
    },
  });
}

export function useUpdateSubCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: Parameters<typeof updateSubCategory>[1];
    }) => updateSubCategory(id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: SUB_CATEGORIES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: [...SUB_CATEGORIES_QUERY_KEY, data.id] });
    },
  });
}

export function useDeleteSubCategoryMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSubCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUB_CATEGORIES_QUERY_KEY });
    },
  });
}

export function useBulkDeleteSubCategoriesMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkDeleteSubCategories,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUB_CATEGORIES_QUERY_KEY });
    },
  });
}

export function useToggleSubCategoryActiveMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      toggleSubCategoryActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUB_CATEGORIES_QUERY_KEY });
    },
  });
}

export function useUploadSubCategoryImageMutation() {
  return useMutation({
    mutationFn: uploadSubCategoryImage,
  });
}

const SUB_CATEGORY_FIELD_LABELS: Record<string, string> = {
  categoryId: "Category",
  cityId: "City",
  name: "Name",
  nameSpanish: "Name (Spanish)",
  description: "Description",
  descriptionSpanish: "Description (Spanish)",
  selectedBannerImageApp: "Selected banner (app)",
  unselectedBannerImageApp: "Unselected banner (app)",
  bannerImageWeb: "Banner image (web)",
  isActive: "Active status",
};

export function getSubCategoryError(error: unknown): string | undefined {
  const err = error as {
    response?: {
      data?: {
        error?: string;
        code?: string;
        hint?: string;
        details?: Array<{ path?: (string | number)[]; message?: string }>;
      };
      status?: number;
    };
  };
  const data = err?.response?.data;
  if (
    data?.code === "VALIDATION_ERROR" &&
    Array.isArray(data?.details) &&
    data.details.length > 0
  ) {
    const messages = data.details
      .map((e: { path?: (string | number)[]; message?: string }) => {
        const field = e.path?.[0];
        const label = typeof field === "string" ? (SUB_CATEGORY_FIELD_LABELS[field] ?? field) : "Field";
        return `${label}: ${e.message ?? "Invalid"}`;
      })
      .join(". ");
    return messages || "Validation failed";
  }
  if (data?.error) return data.error;
  if (data?.code === "DATABASE_NOT_READY")
    return data.hint ?? "Database is being updated. Please try again later.";
  if (err?.response?.status === 401) return "Please sign in to continue.";
  return undefined;
}